import React, { useState, useEffect } from "react";
import { AuthLogo } from "@/components/Auth/AuthLogo";
import { AuthForm } from "@/components/Auth/AuthForm";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { usePageMeta } from "@/hooks/usePageMeta";
import { TurnstileWidget } from "@/components/Auth/TurnstileWidget";

const forgotPasswordSchema = z.object({
  email: z.string().trim().email("Please enter a valid email address"),
});

export default function ForgotPasswordPage() {
  usePageMeta("Forgot Password — Shattara AI", "", { noIndex: true });
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/dashboard");
      }
    });
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");

    // Validate form
    const result = forgotPasswordSchema.safeParse({ email });
    if (!result.success) {
      const errors = result.error.flatten().fieldErrors;
      if (errors.email) setEmailError(errors.email[0]);
      return;
    }

    setIsLoading(true);

    // Verify Turnstile token server-side
    const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-turnstile", {
      body: { token: turnstileToken },
    });
    if (verifyError || !verifyData?.success) {
      toast.error("Bot verification failed. Please try again.");
      setTurnstileToken("");
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success("Password reset link sent! Check your email.");
      setEmail("");
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="flex-grow px-6 flex flex-col items-center justify-center min-h-screen bg-background animate-in fade-in duration-500">
      <div className="w-full max-w-md space-y-6 animate-in slide-in-from-bottom-4 duration-700">
        <div className="flex justify-center">
          <AuthLogo />
        </div>
        
        <section className="flex flex-col items-center justify-center gap-2">
          <h1 className="text-2xl font-semibold">Forgot password?</h1>
          <p className="text-muted-foreground text-center">
            Enter your email and we'll send you a reset link.
          </p>

          <AuthForm
            view="forgot"
            email={email}
            setEmail={setEmail}
            emailError={emailError}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            turnstileToken={turnstileToken}
            setTurnstileToken={setTurnstileToken}
          />

          <div className="mt-4 text-muted-foreground text-sm">
            Remembered your password?{" "}
            <Link
              to="/auth/signin"
              className="text-foreground underline hover:text-primary transition-colors duration-200"
            >
              Sign in
            </Link>
          </div>

          <TurnstileWidget
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken("")}
            onError={() => setTurnstileToken("")}
          />
        </section>
      </div>
    </main>
  );
}
